/**
 * MoneyMart web — session store.
 *
 * Tracks the acting user so every surface (bid, wallet, orders) reads the same
 * identity. Sign-in is passwordless (§9.1): a one-time code is requested for an
 * email address or phone number, then exchanged for a session token.
 *
 * `status` is explicit rather than derived from `user === null`, because
 * "still finding out" and "definitely signed out" drive different UI: the first
 * must not flash a sign-in prompt at a returning user.
 */
import { create } from 'zustand';
import type { PublicProfile, User, UserPreferences } from '@moneymart/types';
import { api } from '@/lib/api';
import { clearToken, getToken, setToken } from '@/lib/token';
import { refreshSocketAuth } from '@/lib/socket';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous';

/** A pending sign-in: a code has been requested and is awaiting entry. */
export interface PendingChallenge {
  identifier: string;
  kind: 'email' | 'phone';
  destination: string;
  expiresInSeconds: number;
  delivery: { delivered: boolean; transport: string; detail?: string };
  devCode?: string;
}

export interface SessionState {
  user: User | null;
  profile: PublicProfile | null;
  status: SessionStatus;
  /** Development-only identity list, for driving multi-user flows. */
  users: PublicProfile[];
  pending: PendingChallenge | null;
  busy: boolean;
  error: string | null;

  refresh: () => Promise<void>;
  requestCode: (identifier: string) => Promise<boolean>;
  verifyCode: (code: string) => Promise<boolean>;
  cancelChallenge: () => void;
  /**
   * Adopt a session token produced by a social sign-in redirect.
   *
   * The token arrives in the URL fragment, which the browser never sends to a
   * server, so it must be read and then removed from the address bar.
   */
  adoptToken: (token: string) => Promise<void>;
  /**
   * Ask the shell to open the sign-in panel.
   *
   * Needed because a failed social handoff lands on whatever page the user
   * started from, where no panel is open — so the reason would be stored and
   * never seen. Consumed and cleared by the shell.
   */
  signInRequested: boolean;
  requestSignIn: (reason?: string) => void;
  clearSignInRequest: () => void;
  /**
   * Persist a change to the signed-in account.
   *
   * Returns the server's answer rather than assuming success: the whole point of
   * this action is that the previous behaviour silently discarded the change.
   */
  saveAccount: (patch: {
    displayName?: string;
    bio?: string;
    countryCode?: string;
    currencyCode?: string;
    avatarUrl?: string;
    preferences?: Partial<UserPreferences>;
  }) => Promise<{ ok: boolean; error?: string }>;
  signOut: () => Promise<void>;
  /** Development-only: switch identity without a code. */
  switchUser: (userId: string) => Promise<void>;
  loadUsers: () => Promise<void>;
}

export const useSession = create<SessionState>((set, get) => ({
  user: null,
  profile: null,
  // Start in `loading` so the shell does not render a signed-out header before
  // the stored token has been checked.
  status: 'loading',
  users: [],
  pending: null,
  busy: false,
  error: null,

  refresh: async () => {
    // No token means definitively signed out — skip the request entirely.
    if (!getToken()) {
      set({ user: null, profile: null, status: 'anonymous', error: null });
      return;
    }
    try {
      const session = await api.authSession();
      set({ user: session.user, profile: session.profile, status: 'authenticated', error: null });
    } catch {
      // The request layer clears a token the server rejected, so falling back to
      // anonymous here is accurate rather than pessimistic.
      clearToken();
      set({ user: null, profile: null, status: 'anonymous' });
    }
  },

  requestCode: async (identifier: string) => {
    set({ busy: true, error: null });
    try {
      const result = await api.requestCode(identifier);
      set({
        busy: false,
        pending: {
          identifier,
          kind: result.kind,
          destination: result.destination,
          expiresInSeconds: result.expiresInSeconds,
          delivery: result.delivery,
          devCode: result.devCode,
        },
      });
      return true;
    } catch (cause) {
      set({
        busy: false,
        error: cause instanceof Error ? cause.message : 'Could not send a code to that address.',
      });
      return false;
    }
  },

  verifyCode: async (code: string) => {
    const pending = get().pending;
    if (!pending) {
      set({ error: 'Request a code first.' });
      return false;
    }
    set({ busy: true, error: null });
    try {
      const result = await api.verifyCode(pending.identifier, code);
      setToken(result.token, result.expiresAt);
      // Re-handshake so the realtime connection carries the new identity.
      refreshSocketAuth();
      set({
        busy: false,
        pending: null,
        user: result.user,
        profile: result.profile,
        status: 'authenticated',
      });
      return true;
    } catch (cause) {
      set({
        busy: false,
        error: cause instanceof Error ? cause.message : 'That code was not accepted.',
      });
      return false;
    }
  },

  cancelChallenge: () => set({ pending: null, error: null }),

  saveAccount: async (patch) => {
    set({ busy: true, error: null });
    try {
      const result = await api.updateMe(patch);
      // The response carries the authoritative record, so the shell updates without
      // a second round trip and cannot drift from what was stored.
      set({ busy: false, user: result.user, profile: result.profile, error: null });
      return { ok: true };
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Could not save those changes.';
      set({ busy: false, error: message });
      return { ok: false, error: message };
    }
  },

  signOut: async () => {
    try {
      await api.signOut();
    } catch {
      // Revoking server-side can fail if the token already expired; local state
      // must be cleared either way or the user stays stuck signed in.
    }
    clearToken();
    refreshSocketAuth();
    set({ user: null, profile: null, status: 'anonymous', pending: null, error: null });
  },

  signInRequested: false,

  requestSignIn: (reason?: string) =>
    set({ signInRequested: true, ...(reason ? { error: reason } : {}) }),

  clearSignInRequest: () => set({ signInRequested: false }),

  adoptToken: async (token: string) => {
    setToken(token);
    refreshSocketAuth();
    set({ status: 'loading', error: null });
    await get().refresh();
  },

  switchUser: async (userId: string) => {
    // Development helper relying on the demo header, which the API refuses in
    // production — so this is expected to fail there and reports that honestly.
    const session = await api.switchUser(userId);
    set({ user: session.user, profile: session.profile, status: 'authenticated' });
  },

  loadUsers: async () => {
    try {
      set({ users: await api.users() });
    } catch {
      set({ users: [] });
    }
  },
}));

export default useSession;
